import type { ApiError as ApiErrorBody } from '../../../shared/dto';

export type ApiRequest<TArgs, TResponse> = (args: TArgs) => Promise<TResponse>;

type CallApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  query?: Record<string, unknown>;
  body?: unknown;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: NonNullable<ApiErrorBody['issues']> = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  issueFor(field: string): string | undefined {
    return this.issues.find((issue) => issue.path === field)?.message;
  }
}

const MENSAGEM_ERRO_GENERICA = 'Não foi possível concluir a ação. Tente novamente.';

export function paraApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }
  const mensagem =
    error instanceof Error && error.message.trim() ? error.message : MENSAGEM_ERRO_GENERICA;
  return new ApiError(0, 'network_error', mensagem);
}

export async function callApi<TResponse>({
  method = 'GET',
  path,
  query,
  body,
}: CallApiOptions): Promise<TResponse> {
  const url = new URL(`/api${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload as ApiErrorBody | null;

    if (response.status === 401) {
      window.dispatchEvent(new Event('kflow:unauthorized'));
    }

    throw new ApiError(
      response.status,
      error?.error ?? 'unknown_error',
      error?.message ?? `A chamada ${method} ${path} falhou com status ${response.status}.`,
      error?.issues ?? [],
    );
  }

  return payload as TResponse;
}
